import React, {useCallback, useEffect, useRef, useState} from "react";
import {Table} from "react-bootstrap";
import {useNavigate, useParams} from "react-router";
import AdminApi from "common/api";
import Tabs from "common/components/Tabs";
import Pagination from "common/components/Pagination";
import {dateFormat} from "common/utils";

const PAGE_SIZE = 30;

function UserInfoBar({user}) {
  if (!user) return null;

  const items = [
    {label: "회원번호", value: user.userNo},
    {label: "닉네임", value: user.nickname || "-"},
    {label: "로그인", value: user.loginType || "-"},
    {label: "포인트", value: (user.point ?? 0).toLocaleString()},
    {label: "간식", value: user.snackCount ?? 0},
    {label: "상태", value: user.status || "-"},
    {label: "가입일", value: dateFormat(user.createdAt, true)},
  ];

  return (
    <div className="user-info-bar">
      {items.map((item) => (
        <div key={item.label} className="user-info-item">
          <span className="user-info-label">{item.label}</span>
          <span className="user-info-value">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function PointHistoryTab({userNo}) {
  const api = new AdminApi();
  const currentPage = useRef(1);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const fetchHistory = useCallback((page = 1) => {
    api.getAdminUserPointHistory(userNo, {page, countPerPage: PAGE_SIZE})
      .then((res) => {
        if (res?.result === "ok") {
          setItems(res.infos ?? []);
          setTotalCount(res.pageInfo?.totalItemCount ?? 0);
          currentPage.current = page;
        } else {
          setItems([]);
          setTotalCount(0);
        }
      });
  }, [userNo]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  return (
    <div>
      <Table bordered className="basic-table user-history-table">
        <thead>
          <tr>
            <th>일시</th>
            <th>변동</th>
            <th>내용</th>
            <th>유형</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? items.map((item, idx) => (
            <tr key={`${item.createdAt}-${idx}`}>
              <td>{dateFormat(item.createdAt, true)}</td>
              <td className={item.amount > 0 ? "text-plus" : "text-minus"}>
                {item.amount > 0 ? "+" : ""}{item.amount?.toLocaleString()}
              </td>
              <td>{item.description || "-"}</td>
              <td>{item.type || "-"}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={4} style={{textAlign: "center"}}>포인트 내역이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalCount > 0 &&
        <Pagination
          total={totalCount}
          pageSize={PAGE_SIZE}
          page={currentPage.current}
          onPageChange={fetchHistory}
        />
      }
    </div>
  );
}

function SnackHistoryTab({userNo}) {
  const api = new AdminApi();
  const currentPage = useRef(1);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const fetchHistory = useCallback((page = 1) => {
    api.getAdminUserSnackHistory(userNo, {page, rowPerPage: PAGE_SIZE})
      .then((res) => {
        if (res?.result === "ok") {
          setItems(res.list ?? []);
          setTotalCount(res.pageInfo?.totalItemCount ?? 0);
          currentPage.current = page;
        } else {
          setItems([]);
          setTotalCount(0);
        }
      });
  }, [userNo]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  return (
    <div>
      <Table bordered className="basic-table user-history-table">
        <thead>
          <tr>
            <th>일시</th>
            <th>변동</th>
            <th>구분</th>
            <th>항목</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? items.map((item) => (
            <tr key={item.idx}>
              <td>{dateFormat(item.createdAt, true)}</td>
              <td className={item.delta > 0 ? "text-plus" : "text-minus"}>
                {item.delta > 0 ? "+" : ""}{item.delta}
              </td>
              <td>{item.itemTitle || "-"}</td>
              <td>{item.itemName || "-"}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={4} style={{textAlign: "center"}}>간식 내역이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalCount > 0 &&
        <Pagination
          total={totalCount}
          pageSize={PAGE_SIZE}
          page={currentPage.current}
          onPageChange={fetchHistory}
        />
      }
    </div>
  );
}

export default function UserDetail() {
  const api = new AdminApi();
  const navigate = useNavigate();
  const {userNo} = useParams();
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.getAdminUser(userNo).then((res) => {
      if (res?.result === "ok") {
        setUser(res.user);
      }
    });
  }, [userNo]);

  return (
    <div className="user-admin">
      <div className="tab-depth-line">
        <h1 onClick={() => navigate("/user")} style={{cursor: "pointer"}}>사용자 관리</h1>
        <h1>&gt; {user?.nickname || `회원 #${userNo}`}</h1>
      </div>

      <UserInfoBar user={user}/>

      <Tabs
        className="user-detail-tabs"
        tabNames={["포인트 내역", "간식 내역"]}
        tabPages={[
          <PointHistoryTab key="point" userNo={Number(userNo)}/>,
          <SnackHistoryTab key="snack" userNo={Number(userNo)}/>,
        ]}
      />
    </div>
  );
}
